// Where a model sits on the site. `collection` picks the page — the anatomy
// library at / or the embryology area at /embryology — and `section` is the
// divider it sits under there. Shared by the API (validation) and the UI.

export const COLLECTIONS = ["anatomy", "embryology"] as const;
export type Collection = (typeof COLLECTIONS)[number];

export const COLLECTION_LABELS: Record<Collection, string> = {
  anatomy: "Anatomy",
  embryology: "Embryology",
};

export type Section = { key: string; label: string };

/** Display order is head to toe for anatomy, early to late for embryology. */
export const SECTIONS: Record<Collection, readonly Section[]> = {
  anatomy: [
    { key: "head-neck", label: "Head & neck" },
    { key: "skull-teeth", label: "Skull & teeth" },
    { key: "eye-ear", label: "Eye & ear" },
    { key: "nervous-system", label: "Nervous system" },
    { key: "torso", label: "Torso" },
    { key: "thorax", label: "Thorax" },
    { key: "abdomen", label: "Abdomen" },
    { key: "pelvis", label: "Pelvis" },
    { key: "upper-limb", label: "Upper limb" },
    { key: "lower-limb", label: "Lower limb" },
  ],
  embryology: [
    { key: "embryo", label: "The embryo" },
    { key: "pregnancy", label: "Pregnancy & the fetus" },
  ],
};

/** Models with no section, or one this version doesn't know, file under this. */
export const OTHER_SECTION: Section = { key: "other", label: "Other" };

export function isCollection(value: unknown): value is Collection {
  return typeof value === "string" && (COLLECTIONS as readonly string[]).includes(value);
}

export function isSection(collection: Collection, key: string): boolean {
  return SECTIONS[collection].some((s) => s.key === key);
}

export function sectionLabel(collection: Collection, key: string | null | undefined): string | null {
  return SECTIONS[collection].find((s) => s.key === key)?.label ?? null;
}

export type SectionGroup<T> = Section & { items: T[] };

/**
 * Split models into the collection's sections, in display order, names sorted
 * naturally ("Lung 2" before "Lung 10"). Empty sections are dropped; anything
 * unfiled trails as "Other".
 */
export function groupBySection<T extends { name: string; section: string | null }>(
  collection: Collection,
  items: T[],
): SectionGroup<T>[] {
  const byName = (a: T, b: T) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
  const groups: SectionGroup<T>[] = SECTIONS[collection].map((s) => ({ ...s, items: [] }));
  const other: SectionGroup<T> = { ...OTHER_SECTION, items: [] };
  for (const item of items) {
    (groups.find((g) => g.key === item.section) ?? other).items.push(item);
  }
  return [...groups, other].filter((g) => g.items.length > 0).map((g) => ({ ...g, items: g.items.sort(byName) }));
}
